// Native-language landing pages for recruiters in rich, self-serve markets
// (Deepak, 2026-10-23: German, French, Spanish, Swedish, Italian). One template
// (app/native/page.jsx), one entry per language, each on its own URL with
// hreflang links to the others and to the English home page. The rest of the
// site stays in English. Every claim mirrors the English site as it ships:
// homepage promise, /pricing (Free 10 credits, Pro $99), /languages (report
// language, what stays in English). Change the English source first.
//
// English master, for reviewers (not rendered):
//   h1: AI takes the first round, in <language>.
//   lead: Every applicant is read against the role. The ones above your bar get
//     a spoken first round interview in <language> within hours, from an AI
//     interviewer. You only read the shortlist.
//   steps: add the role and applicants (CVs scored 0 to 100) / the best take an
//     AI interview in the browser with follow-up questions / you read a ranked
//     shortlist with report, transcript and recording; a person decides.
//   language: speaks and listens in <language> for <countries>; 75 languages in
//     one account; questions written in the chosen language.
//   dashboard: in <language>, chosen by the sign-up link or the language
//     switcher in the top bar. Scored report: in the recruiter's dashboard
//     language (transcript stays in the spoken language). Invitation email: in
//     the interview's language (WhatsApp invites go to English interviews only,
//     so none is claimed here).
//   still English: interview room buttons and on-screen instructions, the PDF
//     download, a few settings screens (career page builder, integrations,
//     billing, profile).
//   pricing: Free 10 interview credits a month; Pro $99 a month, 200 interview
//     credits, 5,000 resumes a month, 30 roles, 5 seats; US dollars, by card.

export const NATIVE_LANDINGS = [
  {
    code: "de",
    hreflang: "de",
    path: "/de",
    nativeName: "Deutsch",
    banner: "Diese Seite gibt es auch auf Deutsch",
    title: "KI-Erstinterviews auf Deutsch für Recruiter",
    description:
      "Jede Bewerbung wird mit der Stelle abgeglichen, wer über Ihrer Schwelle liegt, führt ein KI-Erstinterview auf Deutsch, und Sie lesen nur die Shortlist.",
    kicker: "Für Recruiter und Personalverantwortliche",
    h1: "Die KI führt die erste Runde, auf Deutsch.",
    lead:
      "Jede Bewerbung wird mit der Stelle abgeglichen. Wer über Ihrer Schwelle liegt, erhält innerhalb weniger Stunden ein gesprochenes Erstinterview auf Deutsch mit einem KI-Interviewer. Sie lesen nur noch die Shortlist.",
    ctaStart: "Kostenlos starten",
    ctaDemo: "Demo buchen",
    ctaNote: "Kostenloser Plan mit 10 Interview-Credits pro Monat. Das Dashboard gibt es auf Deutsch.",
    stepsTitle: "So funktioniert es",
    steps: [
      {
        title: "Stelle und Bewerbungen anlegen",
        text: "Laden Sie die Stellenbeschreibung und die Lebensläufe hoch oder teilen Sie Ihren Bewerbungslink. Jeder Lebenslauf erhält eine Bewertung von 0 bis 100 für die Stelle.",
      },
      {
        title: "Wer über Ihrer Schwelle liegt, führt ein KI-Interview",
        text: "Wer über Ihrer Schwelle liegt, erhält eine Einladung und führt das Interview im Browser, auf Deutsch und wann immer es passt. Der KI-Interviewer stellt Rückfragen zu den Antworten.",
      },
      {
        title: "Sie lesen eine sortierte Shortlist",
        text: "Zu jeder Person erhalten Sie einen bewerteten Bericht, das Transkript und, wenn vorhanden, die Aufzeichnung. Wer weiterkommt, entscheidet ein Mensch in Ihrem Team.",
      },
    ],
    langTitle: "Interviews in der Sprache Ihrer Kandidaten",
    langText: [
      "Der KI-Interviewer spricht und versteht Deutsch. Sie wählen Deutschland, Österreich oder die Schweiz, damit Stimme und Spracherkennung zur Region passen.",
      "Dasselbe Konto führt Interviews in 75 Sprachen, zum Beispiel eine Stelle in Wien auf Deutsch und eine in Madrid auf Spanisch. Die Interviewfragen werden in der gewählten Sprache geschrieben.",
    ],
    englishTitle: "Was noch auf Englisch ist",
    englishItems: [
      "Die Schaltflächen und Hinweise im Interviewraum. Das Interview selbst wird in der gewählten Sprache geführt.",
      "Der PDF-Download des Berichts.",
      "Einige Einstellungsseiten: Karriereseiten-Builder, Integrationen, Abrechnung und Profil.",
    ],
    priceTitle: "Preise",
    plans: [
      { name: "Free", price: "0 $", text: "10 Interview-Credits pro Monat." },
      { name: "Pro", price: "99 $ pro Monat", text: "200 Interview-Credits, 5.000 Lebensläufe pro Monat, 30 Stellen und 5 Nutzer." },
    ],
    priceNote: "Die Preise sind in US-Dollar. Sie bezahlen per Karte.",
    faqTitle: "Häufige Fragen",
    faq: [
      {
        q: "Kann das Interview auf Deutsch stattfinden?",
        a: "Ja. Wählen Sie beim Anlegen der Stelle Deutsch für Deutschland, Österreich oder die Schweiz. Der Interviewer begrüßt, fragt und stellt Rückfragen auf Deutsch, und die Fragen werden auf Deutsch geschrieben.",
      },
      {
        q: "In welcher Sprache ist der Bericht?",
        a: "In der Sprache Ihres Dashboards. Nutzen Sie das Dashboard auf Deutsch, ist der Bericht auf Deutsch. Das Dashboard startet in der Sprache des Anmeldelinks auf dieser Seite, über den Sprachwähler in der oberen Leiste ändern Sie sie jederzeit. Das Transkript bleibt in der Sprache, die der Kandidat gesprochen hat.",
      },
      {
        q: "Entscheidet die KI, wer eingestellt wird?",
        a: "Nein. Die KI bewertet jedes Interview nach den Kriterien, die Sie festlegen, und begründet jede Bewertung. Die Entscheidung trifft ein Mensch in Ihrem Team.",
      },
      {
        q: "Müssen Kandidaten etwas installieren?",
        a: "Nein. Kandidaten öffnen den Link aus der Einladung und führen das Interview im Browser. Die Einladung kommt per E-Mail in der Interviewsprache.",
      },
      {
        q: "Wie fange ich an?",
        a: "Erstellen Sie ein kostenloses Konto, legen Sie eine Stelle an und wählen Sie Deutsch als Interviewsprache. Sie können auch eine Demo buchen.",
      },
    ],
    footerLanguages: "Alle Interviewsprachen (Englisch)",
    footerPrivacy: "Datenschutz (Englisch)",
    footerContact: "Kontakt",
    switcherLabel: "Sprache",
  },
  {
    code: "fr",
    hreflang: "fr",
    path: "/fr",
    nativeName: "Français",
    banner: "Cette page existe aussi en français",
    title: "Entretiens IA en français pour les recruteurs",
    description:
      "Chaque candidature est comparée au poste, ceux qui dépassent votre seuil passent un premier entretien IA en français et vous lisez seulement la liste courte.",
    kicker: "Pour les recruteurs et les équipes RH",
    h1: "L'IA mène le premier entretien, en français.",
    lead:
      "Chaque candidature est comparée au poste. Les candidats au-dessus de votre seuil passent en quelques heures un premier entretien oral en français avec un intervieweur IA. Vous lisez seulement la liste courte.",
    ctaStart: "Commencer gratuitement",
    ctaDemo: "Réserver une démo",
    ctaNote: "Offre gratuite avec 10 crédits d'entretien par mois. Le tableau de bord existe en français.",
    stepsTitle: "Comment ça marche",
    steps: [
      {
        title: "Ajoutez le poste et les candidatures",
        text: "Importez la fiche de poste et les CV, ou partagez votre lien de candidature. Chaque CV reçoit une note de 0 à 100 par rapport au poste.",
      },
      {
        title: "Ceux qui dépassent votre seuil passent un entretien IA",
        text: "Les candidats au-dessus de votre seuil reçoivent une invitation et passent un entretien oral dans leur navigateur, en français, au moment qui leur convient. L'intervieweur IA pose des questions de relance.",
      },
      {
        title: "Vous lisez une liste classée",
        text: "Pour chaque candidat, vous recevez un rapport noté, la transcription et l'enregistrement quand il existe. C'est une personne de votre équipe qui décide de la suite.",
      },
    ],
    langTitle: "Des entretiens dans la langue de vos candidats",
    langText: [
      "L'intervieweur IA mène l'entretien en français. Vous choisissez la France, la Belgique, la Suisse ou le Canada, pour que la voix et la reconnaissance vocale correspondent à la région.",
      "Le même compte mène des entretiens dans 75 langues, par exemple un poste à Genève en français et un poste à Zurich en allemand. Les questions d'entretien sont rédigées dans la langue choisie.",
    ],
    englishTitle: "Ce qui est encore en anglais",
    englishItems: [
      "Les boutons et les consignes à l'écran dans la salle d'entretien. L'entretien lui-même se déroule dans la langue choisie.",
      "Le téléchargement du rapport en PDF.",
      "Quelques écrans de réglages : le créateur de page carrières, les intégrations, la facturation et le profil.",
    ],
    priceTitle: "Tarifs",
    plans: [
      { name: "Free", price: "0 $", text: "10 crédits d'entretien par mois." },
      { name: "Pro", price: "99 $ par mois", text: "200 crédits d'entretien, 5 000 CV par mois, 30 postes et 5 utilisateurs." },
    ],
    priceNote: "Les prix sont en dollars américains. Vous payez par carte.",
    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "L'entretien peut-il se dérouler en français ?",
        a: "Oui. Au moment de créer le poste, choisissez le français pour la France, la Belgique, la Suisse ou le Canada. L'intervieweur accueille, interroge et relance alors en français, et les questions sont rédigées en français.",
      },
      {
        q: "Dans quelle langue est le rapport ?",
        a: "Dans la langue de votre tableau de bord. Si vous utilisez le tableau de bord en français, le rapport est en français. Le tableau de bord s'ouvre dans la langue du lien d'inscription de cette page, et vous pouvez en changer avec le sélecteur de langue de la barre du haut. La transcription reste dans la langue parlée par le candidat.",
      },
      {
        q: "Est-ce l'IA qui décide qui est recruté ?",
        a: "Non. L'IA note chaque entretien selon les critères que vous définissez et justifie chaque note. C'est une personne de votre équipe qui décide.",
      },
      {
        q: "Les candidats doivent-ils installer quelque chose ?",
        a: "Non. Les candidats ouvrent le lien de l'invitation et passent l'entretien dans leur navigateur. L'invitation est envoyée par e-mail dans la langue de l'entretien.",
      },
      {
        q: "Comment commencer ?",
        a: "Créez un compte gratuit, ajoutez un poste et choisissez le français comme langue d'entretien. Vous pouvez aussi réserver une démo.",
      },
    ],
    footerLanguages: "Toutes les langues d'entretien (en anglais)",
    footerPrivacy: "Confidentialité (en anglais)",
    footerContact: "Contact",
    switcherLabel: "Langue",
  },
  {
    code: "es",
    hreflang: "es",
    path: "/es",
    nativeName: "Español",
    banner: "Esta página también está en español",
    title: "Entrevistas con IA en español para reclutadores",
    description:
      "Cada candidatura se compara con el puesto, quienes superan tu umbral hacen una primera entrevista con IA en español y tú solo revisas la lista corta.",
    kicker: "Para reclutadores y equipos de selección",
    h1: "La IA hace la primera entrevista, en español.",
    lead:
      "Cada candidatura se compara con el puesto. Quienes superan tu umbral hacen, en pocas horas, una primera entrevista hablada en español con un entrevistador de IA. Tú solo revisas la lista corta.",
    ctaStart: "Empieza gratis",
    ctaDemo: "Reserva una demo",
    ctaNote: "Plan gratuito con 10 créditos de entrevista al mes. El panel está en español.",
    stepsTitle: "Cómo funciona",
    steps: [
      {
        title: "Añade el puesto y las candidaturas",
        text: "Sube la descripción del puesto y los currículums, o comparte tu enlace de candidatura. Cada currículum recibe una puntuación de 0 a 100 frente al puesto.",
      },
      {
        title: "Quienes superan tu umbral hacen una entrevista con IA",
        text: "Quienes superan tu umbral reciben una invitación y hacen una entrevista hablada en el navegador, en español y cuando les venga bien. El entrevistador de IA hace preguntas de seguimiento.",
      },
      {
        title: "Revisas una lista ordenada",
        text: "De cada candidato recibes un informe puntuado, la transcripción y la grabación cuando está disponible. Quién sigue adelante lo decide una persona de tu equipo.",
      },
    ],
    langTitle: "Entrevistas en el idioma de tus candidatos",
    langText: [
      "El entrevistador de IA habla y entiende español. Puedes elegir la variante de España, de México o de otro país hispanohablante, para que la voz y el reconocimiento de voz encajen con la región.",
      "La misma cuenta hace entrevistas en 75 idiomas, por ejemplo un puesto en Madrid en español de España y otro en Ciudad de México en español de México. Las preguntas se escriben en el idioma que elijas.",
    ],
    englishTitle: "Lo que todavía está en inglés",
    englishItems: [
      "Los botones y las instrucciones en pantalla de la sala de entrevista. La entrevista hablada sí es en el idioma elegido.",
      "La descarga del informe en PDF.",
      "Algunas pantallas de ajustes: el creador de la página de empleo, las integraciones, la facturación y el perfil.",
    ],
    priceTitle: "Precios",
    plans: [
      { name: "Free", price: "0 $", text: "10 créditos de entrevista al mes." },
      { name: "Pro", price: "99 $ al mes", text: "200 créditos de entrevista, 5.000 currículums al mes, 30 puestos y 5 usuarios." },
    ],
    priceNote: "Los precios están en dólares estadounidenses. Pagas con tarjeta.",
    faqTitle: "Preguntas frecuentes",
    faq: [
      {
        q: "¿La entrevista puede ser en español?",
        a: "Sí. Al crear el puesto, elige la variante de español de España, México u otro país. El entrevistador saluda, pregunta y repregunta en español, y las preguntas se escriben en español.",
      },
      {
        q: "¿En qué idioma está el informe?",
        a: "En el idioma de tu panel. Si usas el panel en español, el informe está en español. El panel se abre en el idioma del enlace de registro de esta página, y puedes cambiarlo con el selector de idioma de la barra superior. La transcripción queda en el idioma que habló el candidato.",
      },
      {
        q: "¿La IA decide a quién se contrata?",
        a: "No. La IA puntúa cada entrevista según los criterios que defines y explica cada puntuación. La decisión la toma una persona de tu equipo.",
      },
      {
        q: "¿Los candidatos tienen que instalar algo?",
        a: "No. Los candidatos abren el enlace de la invitación y hacen la entrevista en el navegador. La invitación se envía por correo electrónico en el idioma de la entrevista.",
      },
      {
        q: "¿Cómo empiezo?",
        a: "Crea una cuenta gratuita, añade un puesto y elige español como idioma de la entrevista. También puedes reservar una demo.",
      },
    ],
    footerLanguages: "Todos los idiomas de entrevista (en inglés)",
    footerPrivacy: "Privacidad (en inglés)",
    footerContact: "Contacto",
    switcherLabel: "Idioma",
  },
  {
    code: "sv",
    hreflang: "sv",
    path: "/sv",
    nativeName: "Svenska",
    banner: "Den här sidan finns också på svenska",
    title: "AI-intervjuer på svenska för rekryterare",
    description:
      "Varje ansökan jämförs med tjänsten, de som ligger över din gräns gör en första AI-intervju på svenska och du läser bara kortlistan.",
    kicker: "För rekryterare och HR-team",
    h1: "AI sköter den första intervjun, på svenska.",
    lead:
      "Varje ansökan jämförs med tjänsten. De som ligger över din gräns får inom några timmar en muntlig första intervju på svenska med en AI-intervjuare. Du läser bara kortlistan.",
    ctaStart: "Kom igång gratis",
    ctaDemo: "Boka en demo",
    ctaNote: "Gratisplan med 10 intervjukrediter i månaden. Översikten finns på svenska.",
    stepsTitle: "Så fungerar det",
    steps: [
      {
        title: "Lägg till tjänsten och kandidaterna",
        text: "Ladda upp annonsen och CV:na, eller dela din ansökningslänk. Varje CV får ett betyg från 0 till 100 mot tjänsten.",
      },
      {
        title: "De som ligger över din gräns gör en AI-intervju",
        text: "De som ligger över din gräns får en inbjudan och gör en muntlig intervju i webbläsaren, på svenska och när det passar dem. AI-intervjuaren ställer följdfrågor.",
      },
      {
        title: "Du läser en rangordnad kortlista",
        text: "För varje kandidat får du en poängsatt rapport, transkriptet och inspelningen när den finns. En person i ditt team bestämmer vem som går vidare.",
      },
    ],
    langTitle: "Intervjuer på dina kandidaters språk",
    langText: [
      "AI-intervjuaren talar och förstår svenska, med en röst och taligenkänning för Sverige.",
      "Med samma konto kan du hålla intervjuer på 75 språk, till exempel en tjänst i Stockholm på svenska och en i Oslo på norska. Intervjufrågorna skrivs på det språk du väljer.",
    ],
    englishTitle: "Det här är fortfarande på engelska",
    englishItems: [
      "Knapparna och instruktionerna på skärmen i intervjurummet. Själva intervjun hålls på det valda språket.",
      "PDF-nedladdningen av rapporten.",
      "Några inställningssidor: byggaren för karriärsidan, integrationer, fakturering och profil.",
    ],
    priceTitle: "Priser",
    plans: [
      { name: "Free", price: "0 $", text: "10 intervjukrediter i månaden." },
      { name: "Pro", price: "99 $ i månaden", text: "200 intervjukrediter, 5 000 CV i månaden, 30 tjänster och 5 användare." },
    ],
    priceNote: "Priserna är i amerikanska dollar. Du betalar med kort.",
    faqTitle: "Vanliga frågor",
    faq: [
      {
        q: "Kan intervjun hållas på svenska?",
        a: "Ja. Välj svenska när du skapar tjänsten. Intervjuaren hälsar, frågar och ställer följdfrågor på svenska, och frågorna skrivs på svenska.",
      },
      {
        q: "Vilket språk är rapporten på?",
        a: "På språket i din översikt. Använder du översikten på svenska är rapporten på svenska. Översikten startar på språket i registreringslänken på den här sidan, och du byter med språkväljaren i det övre fältet. Transkriptet stannar på det språk kandidaten talade.",
      },
      {
        q: "Bestämmer AI vem som anställs?",
        a: "Nej. AI poängsätter varje intervju efter de kriterier du sätter och förklarar varje poäng. Beslutet fattas av en person i ditt team.",
      },
      {
        q: "Behöver kandidaterna installera något?",
        a: "Nej. Kandidaterna öppnar länken i inbjudan och gör intervjun i webbläsaren. Inbjudan skickas via e-post på intervjuns språk.",
      },
      {
        q: "Hur kommer jag igång?",
        a: "Skapa ett gratis konto, lägg till en tjänst och välj svenska som intervjuspråk. Du kan också boka en demo.",
      },
    ],
    footerLanguages: "Alla intervjuspråk (på engelska)",
    footerPrivacy: "Integritet (på engelska)",
    footerContact: "Kontakt",
    switcherLabel: "Språk",
  },
  {
    code: "it",
    hreflang: "it",
    path: "/it",
    nativeName: "Italiano",
    banner: "Questa pagina è disponibile anche in italiano",
    title: "Colloqui con IA in italiano per i recruiter",
    description:
      "Ogni candidatura viene confrontata con la posizione, chi supera la tua soglia fa un primo colloquio con IA in italiano e tu leggi solo la shortlist.",
    kicker: "Per recruiter e team HR",
    h1: "L'IA fa il primo colloquio, in italiano.",
    lead:
      "Ogni candidatura viene confrontata con la posizione. Chi supera la tua soglia può fare, nel giro di poche ore, un primo colloquio a voce in italiano con un intervistatore IA. Tu leggi solo la shortlist.",
    ctaStart: "Inizia gratis",
    ctaDemo: "Prenota una demo",
    ctaNote: "Piano gratuito con 10 crediti di colloquio al mese. La dashboard è in italiano.",
    stepsTitle: "Come funziona",
    steps: [
      {
        title: "Aggiungi la posizione e le candidature",
        text: "Carica la descrizione della posizione e i CV, oppure condividi il tuo link di candidatura. Ogni CV riceve un punteggio da 0 a 100 rispetto alla posizione.",
      },
      {
        title: "Chi supera la tua soglia fa un colloquio con IA",
        text: "Chi supera la tua soglia riceve un invito e fa un colloquio a voce nel browser, in italiano e quando gli è comodo. L'intervistatore IA fa domande di approfondimento.",
      },
      {
        title: "Leggi una shortlist ordinata",
        text: "Per ogni candidato ricevi un report con punteggio, la trascrizione e la registrazione quando disponibile. Chi va avanti lo decide una persona del tuo team.",
      },
    ],
    langTitle: "Colloqui nella lingua dei tuoi candidati",
    langText: [
      "L'intervistatore IA conduce il colloquio in italiano, con una voce e un riconoscimento vocale per l'italiano d'Italia.",
      "Lo stesso account fa colloqui in 75 lingue, per esempio una posizione a Milano in italiano e una a Zurigo in tedesco. Le domande del colloquio sono scritte nella lingua che scegli.",
    ],
    englishTitle: "Cosa è ancora in inglese",
    englishItems: [
      "I pulsanti e le istruzioni a schermo nella sala del colloquio. Il colloquio parlato si svolge invece nella lingua scelta.",
      "Il download del report in PDF.",
      "Alcune schermate delle impostazioni: il creatore della pagina carriere, le integrazioni, la fatturazione e il profilo.",
    ],
    priceTitle: "Prezzi",
    plans: [
      { name: "Free", price: "0 $", text: "10 crediti di colloquio al mese." },
      { name: "Pro", price: "99 $ al mese", text: "200 crediti di colloquio, 5.000 CV al mese, 30 posizioni e 5 utenti." },
    ],
    priceNote: "I prezzi sono in dollari statunitensi. Paghi con carta.",
    faqTitle: "Domande frequenti",
    faq: [
      {
        q: "Il colloquio può essere in italiano?",
        a: "Sì. Quando crei la posizione, scegli l'italiano. L'intervistatore saluta, fa le domande e approfondisce in italiano, e le domande sono scritte in italiano.",
      },
      {
        q: "In che lingua è il report?",
        a: "Nella lingua della tua dashboard. Se usi la dashboard in italiano, il report è in italiano. La dashboard si apre nella lingua del link di registrazione di questa pagina e puoi cambiarla con il selettore della lingua nella barra in alto. La trascrizione resta nella lingua parlata dal candidato.",
      },
      {
        q: "È l'IA a decidere chi viene assunto?",
        a: "No. L'IA valuta ogni colloquio secondo i criteri che stabilisci e spiega ogni punteggio. La decisione la prende una persona del tuo team.",
      },
      {
        q: "I candidati devono installare qualcosa?",
        a: "No. I candidati aprono il link dell'invito e fanno il colloquio nel browser. L'invito arriva via e-mail nella lingua del colloquio.",
      },
      {
        q: "Come inizio?",
        a: "Crea un account gratuito, aggiungi una posizione e scegli l'italiano come lingua del colloquio. Puoi anche prenotare una demo.",
      },
    ],
    footerLanguages: "Tutte le lingue dei colloqui (in inglese)",
    footerPrivacy: "Privacy (in inglese)",
    footerContact: "Contatti",
    switcherLabel: "Lingua",
  },
];

export const NATIVE_BY_PATH = Object.fromEntries(NATIVE_LANDINGS.map((l) => [l.path, l]));

// Browser language (navigator.languages) to the native page a visitor on the
// English site is offered. Only a link: nobody is redirected (Google asks for
// separate URLs and no automatic redirects between language versions).
export function nativeLandingFor(languages) {
  for (const tag of languages || []) {
    const base = String(tag || "").toLowerCase().split("-")[0];
    if (base === "en") return null;
    const hit = NATIVE_LANDINGS.find((l) => l.code === base);
    if (hit) return hit;
  }
  return null;
}
